import { useEffect, useRef } from 'react';

import newNotificationSound from 'assets/sounds/new-notification.mp3';

import { useLazyGetNotificationsCountQuery } from 'services/notifications/notifications';
import { UnreadNotificationsCountUpdatedEvent } from 'services/notifications/notifications.types';

import { selectUser } from 'store';
import { setUnreadStatus, triggerIndicatorAnimation } from 'store/notifications/notificationsSlice';

import { useAppDispatch, useAppSelector } from 'hooks';
import { useSelectFeatureStatus } from 'hooks/useSelectFeatureStatus';
import { socketClose, socketInit } from 'socket';
import socketNotifications from 'socket/socketNotifications';
import { getAccessToken, selectSessionScope } from 'utils/authSession';

const NOTIFICATION_VOLUME = 0.5;

export const useNotificationsCount = () => {
  const dispatch = useAppDispatch();
  // Keyed on the session, not the token, so a renewal neither refetches nor reconnects.
  const hasAccessToken = !!useAppSelector(selectUser)?.accessToken;
  const sessionScope = useAppSelector(selectSessionScope);
  const isNotificationsEnabled = useSelectFeatureStatus('notifications', 'enabled');
  const [fetchCount] = useLazyGetNotificationsCountQuery();
  const prevHighPriorityCountRef = useRef(0);
  const audioRef = useRef(new Audio(newNotificationSound));

  useEffect(() => {
    audioRef.current.volume = NOTIFICATION_VOLUME;
  }, []);

  useEffect(() => {
    if (!hasAccessToken || !isNotificationsEnabled) return;

    fetchCount()
      .unwrap()
      .then((data) => {
        dispatch(setUnreadStatus(data.data));
        prevHighPriorityCountRef.current = data.data.highPriorityUnreadNotificationsCount;
      })
      .catch(() => {});

    socketInit(socketNotifications, getAccessToken());

    const handleCountUpdate = (payload: UnreadNotificationsCountUpdatedEvent) => {
      const nextCount = payload.highPriorityUnreadNotificationsCount;
      if (nextCount > prevHighPriorityCountRef.current) {
        dispatch(triggerIndicatorAnimation(true));
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
      prevHighPriorityCountRef.current = nextCount;
      dispatch(setUnreadStatus(payload));
    };

    socketNotifications.on('unread_notifications_count_updated', handleCountUpdate);

    return () => {
      socketNotifications.off('unread_notifications_count_updated', handleCountUpdate);
      socketClose(socketNotifications);
    };
  }, [hasAccessToken, sessionScope, isNotificationsEnabled, dispatch, fetchCount]);
};
